// Input - mirrors focus/value state of [data-input] fields into is-focus /
// is-value classes (styled float labels). Behavioral port of v1 modules/Input.js.

export interface InputConfig {
  selector: string;
  classFocus: string;
  classValue: string;
}

const defaultConfig = {
  classFocus: 'is-focus',
  classValue: 'is-value',
};

export default class Input {
  config: InputConfig;

  items: HTMLInputElement[] = [];

  constructor(selector = '[data-input]', config: Partial<InputConfig> = {}) {
    this.config = { ...defaultConfig, selector, ...config };

    this._init();
  }

  private _init(): void {
    this.items = [...document.querySelectorAll<HTMLInputElement>(this.config.selector)];

    this.items.forEach(item => {
      this._attachEvents(item);

      Input.setClassValue(item, this.config.classValue);
    });
  }

  update(): void {
    this.destroy();
    this._init();
  }

  destroy(): void {
    this.items.forEach(item => {
      this._detachEvents(item);
    });
    this.items = [];
  }

  private _attachEvents(item: HTMLInputElement): void {
    item.addEventListener('focus', this._handleFocus);
    item.addEventListener('blur', this._handleBlur);

    item.addEventListener('change', this._handleChange);
    item.addEventListener('keyup', this._handleChange);
    item.addEventListener('search', this._handleChange);
  }

  private _detachEvents(item: HTMLInputElement): void {
    item.removeEventListener('focus', this._handleFocus);
    item.removeEventListener('blur', this._handleBlur);

    item.removeEventListener('change', this._handleChange);
    item.removeEventListener('keyup', this._handleChange);
    item.removeEventListener('search', this._handleChange);
  }

  private _handleFocus = (e: Event): void => {
    (e.target as Element).classList.add(this.config.classFocus);
  };

  private _handleBlur = (e: Event): void => {
    (e.target as Element).classList.remove(this.config.classFocus);
  };

  private _handleChange = (e: Event): void => {
    Input.setClassValue(e.target as HTMLInputElement, this.config.classValue);
  };

  static setClassValue(
    element: HTMLInputElement,
    classValue: string = defaultConfig.classValue,
  ): void {
    if (element.value) {
      element.classList.add(classValue);
    } else {
      element.classList.remove(classValue);
    }
  }
}
